"use client";

import { NAVIGATION_MENU, NavigationItem } from "@/lib/constants/navigation";
import { MarketFinderNavBadge } from "@/components/layout/MarketFinderNavBadge";
import { getCurrentYear } from "@/lib/utils";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/lib/auth/store";
import { hasScope, matchesScope } from "@/lib/auth/scope-utils";
import { useIsGenciseUser } from "@/lib/hooks/useIsGenciseUser";
import { ChevronDown, LogOut, Menu } from "lucide-react";
import * as LucideIcons from "lucide-react";
import NextImage from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useSidebar } from "@/lib/hooks/useSidebar";
import { getListFilters } from "@/lib/hooks/usePersistedFilters";
import { Typography } from "@/components/common/ui/Typography";
import { Button } from "@/components/forms/Button";

/** Navigate to href, appending any persisted list filters from sessionStorage. */
function useFilteredNav() {
  const router = useRouter();
  return (e: React.MouseEvent, href: string) => {
    const saved = getListFilters(href);
    if (saved) {
      e.preventDefault();
      router.push(`${href}?${saved}`);
    }
    // If no saved filters, let the default <Link> navigation happen
  };
}

export function Sidebar() {
  const currentYear = getCurrentYear();
  const pathname = usePathname();
  const { isCollapsed, toggle } = useSidebar();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const isGenciseUser = useIsGenciseUser();
  const handleFilteredNav = useFilteredNav();

  // Visibility helper applied uniformly to top-level items, children, and
  // grandchildren. Items flagged `requiresGenciseEmail` are hidden unless the
  // user's email is on the gencise.ai domain (AND-gated with the scope check).
  const isVisible = (item: NavigationItem): boolean => {
    if (item.requiresGenciseEmail && !isGenciseUser) return false;
    if (item.excludeIfScope && hasScope(user?.scopes, item.excludeIfScope)) return false;
    if (item.scope && !matchesScope(user?.scopes, item.scope)) return false;
    return true;
  };

  return (
    <aside
      className={cn("h-screen bg-navigator-primary-dark flex flex-col border-r border-navigator-primary/20 transition-all duration-200",
        isCollapsed ? "w-16" : "w-64"
      )}
    >
      <div className={cn("p-6", isCollapsed && "px-3")}>
        <div className={cn("flex items-center overflow-hidden",
          isCollapsed ? "justify-center" : "justify-between"
        )}>
          <div className={cn(
            "flex items-center gap-3 overflow-hidden transition-all duration-200",
            isCollapsed ? "max-w-0 opacity-0" : "max-w-[200px] opacity-100"
          )}>
            <NextImage
              src="/logo-icon.png"
              alt="IQ Suite Logo"
              width={36}
              height={36}
              className="object-contain shrink-0"
            />
            <Typography variant="headline3" weight="bold" color="white" as="span" className="md:text-2xl whitespace-nowrap">
              IQ Suite
            </Typography>
          </div>
          <Button
            iconOnly
            startIcon={<Menu className="w-5 h-5 text-white/80 hover:text-white" />}
            variant="ghost"
            size="sm"
            onClick={toggle}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="shrink-0 p-1.5 hover:bg-white/10 border-transparent"
          />
        </div>
      </div>

      <div className={cn("mb-4", isCollapsed ? "mx-3" : "mx-6")}>
        <div className="h-px bg-linear-to-r from-transparent via-white/20 to-transparent" />
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-2.5">
        {NAVIGATION_MENU.filter(isVisible).map((item) => {
          const Icon = LucideIcons[
            item.icon as keyof typeof LucideIcons
          ] as any;
          const visibleChildren = item.children?.filter((child) => {
            if (!isVisible(child)) return false;
            // If child has its own children, ensure at least one is visible
            if (child.children) {
              const visibleGrandchildren = child.children.filter(isVisible);
              if (visibleGrandchildren.length === 0) return false;
            }
            return true;
          });
          const hasChildren = visibleChildren && visibleChildren.length > 0;

          // Find first leaf href for collapsed mode
          const firstLeafHref = (() => {
            if (!visibleChildren || visibleChildren.length === 0)
              return item.href;
            const first = visibleChildren[0];
            const grandchildren = first.children?.filter(isVisible);
            if (grandchildren && grandchildren.length > 0)
              return grandchildren[0].href;
            return first.href;
          })();

          return (
            <div key={item.id} className="mb-1">
              {hasChildren ? (
                isCollapsed ? (
                  <Link
                    href={firstLeafHref}
                    onClick={(e) => handleFilteredNav(e, firstLeafHref)}
                    title={item.label}
                  >
                    <div
                      className={cn("relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200 overflow-hidden",
                        pathname.startsWith(item.href)
                          ? "bg-amber-500/20 text-amber-400 border-l-2 border-amber-400"
                          : "text-white/90 hover:bg-white/10 hover:text-white",
                      )}
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                      {item.id === "market_finder" && (
                        <MarketFinderNavBadge collapsed />
                      )}
                    </div>
                  </Link>
                ) : (
                  <div className="group/menu">
                    <input
                      type="checkbox"
                      id={`menu-${item.id}`}
                      className="peer hidden"
                      defaultChecked={pathname.startsWith(item.href)}
                    />
                    <label
                      htmlFor={`menu-${item.id}`}
                      className={cn("flex items-center justify-between px-3 py-2.5 rounded-lg text-white/90 hover:bg-white/10 hover:text-white transition-all cursor-pointer",
                        pathname.startsWith(item.href) && "bg-white/10 text-white"
                      )}
                    >
                      <div className="relative flex items-center gap-3">
                        <Icon className="w-5 h-5" />
                        <Typography variant="body" weight="semibold" color="inherit" as="span">
                          {item.label}
                        </Typography>
                        {item.id === "market_finder" && (
                          <MarketFinderNavBadge collapsed={false} />
                        )}
                      </div>
                      <ChevronDown className="w-4 h-4 transition-transform" />
                    </label>

                    <div className="ml-4 grid grid-rows-[0fr] peer-checked:grid-rows-[1fr] peer-checked:mt-1 transition-[grid-template-rows] duration-200">
                      <div className="overflow-hidden space-y-1">
                      {visibleChildren?.map(
                        (child: NavigationItem, childIndex: number) => {
                          const ChildIcon = LucideIcons[
                            child.icon as keyof typeof LucideIcons
                          ] as any;

                          // Check if child has its own visible children (sub-accordion)
                          const grandchildren = child.children?.filter(isVisible);
                          const hasGrandchildren =
                            grandchildren && grandchildren.length > 0;

                          if (hasGrandchildren) {
                            return (
                              <div key={`${item.id}-${childIndex}`}>
                                <input
                                  type="checkbox"
                                  id={`menu-${child.id}`}
                                  className="peer hidden"
                                  defaultChecked={pathname.startsWith(
                                    child.href,
                                  )}
                                />
                                <label
                                  htmlFor={`menu-${child.id}`}
                                  className={cn("flex items-center justify-between px-3 py-2 rounded-lg transition-colors duration-200 cursor-pointer",
                                    pathname.startsWith(child.href)
                                      ? "text-white"
                                      : "text-white/70 hover:bg-white/10 hover:text-white"
                                  )}
                                >
                                  <div className="flex items-center gap-3">
                                    <ChildIcon className="w-4 h-4" />
                                    <Typography variant="body" color="inherit" as="span">
                                      {child.label}
                                    </Typography>
                                  </div>
                                  <ChevronDown className="w-3 h-3 transition-transform" />
                                </label>
                                <div className="ml-4 grid grid-rows-[0fr] peer-checked:grid-rows-[1fr] peer-checked:mt-1 transition-[grid-template-rows] duration-200">
                                  <div className="overflow-hidden space-y-1">
                                  {grandchildren.map((gc, gcIndex) => {
                                    const GcIcon = LucideIcons[
                                      gc.icon as keyof typeof LucideIcons
                                    ] as any;
                                    const isActive = pathname === gc.href;
                                    return (
                                      <Link
                                        key={`${child.id}-${gcIndex}`}
                                        href={gc.href}
                                        onClick={(e) =>
                                          handleFilteredNav(e, gc.href)
                                        }
                                      >
                                        <div
                                          className={cn("flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-200",
                                            isActive
                                              ? "bg-amber-500/20 text-amber-400 border-l-2 border-amber-400"
                                              : "text-white/70 hover:bg-white/10 hover:text-white"
                                          )}
                                        >
                                          <GcIcon className="w-4 h-4" />
                                          <Typography variant="body" color="inherit" as="span">
                                            {gc.label}
                                          </Typography>
                                        </div>
                                      </Link>
                                    );
                                  })}
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          const isActive = pathname === child.href;
                          return (
                            <Link
                              key={`${item.id}-${childIndex}`}
                              href={child.href}
                              onClick={(e) => handleFilteredNav(e, child.href)}
                            >
                              <div
                                className={cn("flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-200",
                                  isActive
                                    ? "bg-amber-500/20 text-amber-400 border-l-2 border-amber-400"
                                    : "text-white/70 hover:bg-white/10 hover:text-white"
                              )}
                              >
                                <ChildIcon className="w-4 h-4" />
                                <Typography variant="body" color="inherit" as="span">
                                  {child.label}
                                </Typography>
                              </div>
                            </Link>
                          );
                        },
                      )}
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <Link
                  href={item.href}
                  onClick={(e) => handleFilteredNav(e, item.href)}
                  title={isCollapsed ? item.label : undefined}
                >
                  <div
                    className={cn("relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200 overflow-hidden",
                      pathname === item.href ||
                      pathname.startsWith(item.href + "/")
                        ? "bg-amber-500/20 text-amber-400 border-l-2 border-amber-400"
                        : "text-white/90 hover:bg-white/10 hover:text-white"
                    )}
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    <Typography
                      variant="body"
                      weight="semibold"
                      color="inherit"
                      as="span"
                      className={cn("whitespace-nowrap transition-all duration-200",
                        isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
                      )}
                    >
                      {item.label}
                    </Typography>
                    {item.id === "market_finder" && (
                      <MarketFinderNavBadge collapsed={isCollapsed} />
                    )}
                  </div>
                </Link>
              )}
            </div>
          );
        })}
      </nav>

      <div
        className={cn("border-t border-navigator-primary/20", isCollapsed ? "p-2" : "p-4")}
      >
        <div
          className={cn("flex items-center rounded-lg bg-white/5",
            isCollapsed ? "justify-center p-2" : "gap-3 px-3 py-2"
          )}
        >
          <Link
            href="/profile"
            className={cn("flex items-center flex-1 min-w-0 hover:opacity-80 transition-opacity",
              isCollapsed ? "justify-center" : "gap-3 overflow-hidden"
            )}
            title="View profile"
          >
            <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
              <Typography as="span" variant="body" weight="medium" color="text-amber-400">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </Typography>
            </div>
            <div className={cn("flex-1 min-w-0 transition-all duration-200",
              isCollapsed ? "max-w-0 opacity-0" : "max-w-[200px] opacity-100"
            )}>
              <Typography variant="body" weight="medium" color="white" as="p" truncate>
                {user?.name || "My Account"}
              </Typography>
              <Typography variant="small" color="text-white/60" as="p" truncate>
                {user?.email || ""}
              </Typography>
            </div>
          </Link>
          <Button
            iconOnly
            startIcon={<LogOut className="w-4 h-4 text-white/60 hover:text-white" />}
            variant="ghost"
            size="sm"
            onClick={logout}
            title="Sign out"
            aria-label="Sign out"
            className={cn("shrink-0 p-1.5 hover:bg-white/10 border-transparent transition-all duration-200",
              isCollapsed ? "hidden" : "max-w-10 opacity-100"
            )}
          />
        </div>
      </div>

      <footer className={cn("border-t border-navigator-primary/20 overflow-hidden transition-all duration-200",
        isCollapsed ? "max-h-0 opacity-0 p-0" : "max-h-16 opacity-100 p-4"
      )}>
        <div className="flex items-center justify-center gap-2 whitespace-nowrap">
          <Typography variant="small" color="text-white/40" as="span">© {currentYear}</Typography>
          <Typography variant="small" color="text-white/30" as="span">•</Typography>
          <Typography variant="small" color="text-white/30" as="span">All rights reserved</Typography>
        </div>
      </footer>
    </aside>
  );
}
